import { Autocomplete, Chip, TextField } from '@mui/material';
import { useState } from 'react';
import CustomSnackbar from '../../CustomSnackbar';

// Email validation regex
export const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

// Validate only email format (no count restriction)
export const validateEmails = (emails) => {
  const invalidEmails = emails.filter((email) => !isValidEmail(email));

  if (emails.length === 0) {
    return ['Please enter at least one recipient.'];
  }

  if (invalidEmails.length > 0) {
    return [`Invalid email(s): ${invalidEmails.join(', ')}`];
  }

  return [];
};

const EmailInput = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const addEmails = (newEmails) => {
    const validEmails = newEmails
      .map((email) => email.trim())
      .filter(isValidEmail);

    const merged = [...value, ...validEmails];
    const unique = Array.from(new Set(merged));

    onChange(unique);
  };

  return (
    <>
      <Autocomplete
        multiple
        limitTags={6}
        freeSolo
        options={[]}
        value={value}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          if (!event) return;

          const lastChar = newInputValue.slice(-1);
          const shouldAdd =
            lastChar === ',' || lastChar === ' ' || lastChar === ';';

          if (event.type === 'change' && shouldAdd) {
            const emails = newInputValue
              .split(/[,;\s]+/)
              .map((e) => e.trim())
              .filter(Boolean);

            addEmails(emails);
            setInputValue('');
          } else {
            setInputValue(newInputValue);
          }
        }}
        onChange={(event, newValue) => {
          const uniqueValidEmails = Array.from(
            new Set(newValue.filter(isValidEmail))
          );
          onChange(uniqueValidEmails);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option}
                variant="outlined"
                size="small"
                clickable={false}
                onClick={(e) => e.stopPropagation()}
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Recipients"
            placeholder="Type or paste emails, separated by comma"
            size="small"
            onPaste={(event) => {
              const paste = event.clipboardData.getData('text');
              const emails = paste
                .split(/[,;\s]+/)
                .map((e) => e.trim())
                .filter(Boolean);

              addEmails(emails);
              event.preventDefault();
              setInputValue('');
            }}
            onBlur={() => {
              if (inputValue.trim() && isValidEmail(inputValue.trim())) {
                addEmails([inputValue]);
                setInputValue('');
              } else if (inputValue.trim()) {
                showSnackbar('Invalid email format');
              }
            }}
          />
        )}
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity="error"
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default EmailInput;
